import React from 'react';
import { Link } from 'react-router-dom';

import { useBreadcrumbs } from '../../store/BreadcrumbContext';
import { navigateToVueRoute } from '../../utils/navigationHandler';

/*
  Breadcrumb styling — mirrors RW-ERP-Service. Parent crumbs are brand-green
  links; the current crumb is bold and uses the primary text color so it reads
  as "you are here" in both themes. Separator is the literal ">" used by Vue,
  rendered via an `after:` arbitrary variant on non-last crumbs (in the brand
  accent color regardless of which crumb owns it).
*/

const BREADCRUMB_NAV_CLASS =
  'text-[13px] py-2 text-[var(--color-text-primary)]';

const BREADCRUMB_LIST_CLASS =
  'flex flex-wrap items-center gap-[6px] list-none m-0 p-0';

const BREADCRUMB_CRUMB_BASE =
  'inline-flex items-center gap-[6px] text-[var(--color-text-primary)] text-[13px] leading-[1.4]';

// Non-last crumb gets the ">" separator via a Tailwind `after:` arbitrary
// variant. `content-['>']` produces the literal chevron and the after: utility
// applies the same margin / brand-accent color / 500 weight the original CSS
// used.
const BREADCRUMB_CRUMB_NOT_LAST =
  ' after:content-[">"] after:ml-[6px] after:text-[var(--color-accent)] after:font-hk-medium' +
  // MOBILE (≤768px): parent crumbs are hidden entirely (their chevrons go
  // with them — the separator lives on the parent's `after:`), so the
  // breadcrumb row shows only the bold leaf and never wraps to a second
  // line next to the Export action (QA 2026-08-21).
  ' max-[768px]:hidden';

const BREADCRUMB_CRUMB_CURRENT =
  ' text-[var(--color-text-primary)] font-hk-semibold' +
  // Mobile: the leaf is the page TITLE (parents are hidden) — bold and a
  // step larger so it reads as a header, not a crumb.
  ' max-[768px]:font-hk-bold max-[768px]:text-[15px]';

const BREADCRUMB_LINK_BASE =
  'text-[var(--color-accent)] font-hk-medium bg-transparent border-0 p-0 cursor-pointer no-underline font-[inherit] text-[inherit] leading-[inherit]' +
  ' hover:text-[var(--color-accent-hover)] hover:underline' +
  ' focus-visible:text-[var(--color-accent-hover)] focus-visible:underline focus-visible:outline-none';

const Breadcrumb: React.FC = () => {
  const { breadcrumbs } = useBreadcrumbs();

  if (breadcrumbs.length === 0) return null;

  return (
    <nav className={BREADCRUMB_NAV_CLASS} aria-label="Breadcrumb">
      <ol className={BREADCRUMB_LIST_CLASS}>
        {breadcrumbs.map((crumb, idx) => {
          const isLast = idx === breadcrumbs.length - 1;

          // Last crumb = leaf "you are here". Plain text, bold, primary color.
          // Mirrors Vue's `<span class="breadcrumb-leaf">`.
          if (isLast) {
            return (
              <li
                key={`${crumb.path}-${idx}`}
                className={BREADCRUMB_CRUMB_BASE + BREADCRUMB_CRUMB_CURRENT}
                aria-current="page"
              >
                {crumb.label}
              </li>
            );
          }

          // Every non-last crumb = green. Render as a Link/button when it has
          // a path, as a non-clickable span otherwise — both share the link
          // styling so the color is identical. Mirrors Vue's
          // `<span class="breadcrumb">` (green) regardless of whether it sits
          // inside a router-link or a plain div.
          let inner: React.ReactNode;
          if (!crumb.path) {
            inner = <span className={BREADCRUMB_LINK_BASE}>{crumb.label}</span>;
          } else if (crumb.isReactRoute) {
            inner = (
              <Link to={crumb.path} className={BREADCRUMB_LINK_BASE}>
                {crumb.label}
              </Link>
            );
          } else {
            inner = (
              <button
                type="button"
                className={BREADCRUMB_LINK_BASE}
                /* Cross-origin crumb → new tab, matching the menus: this tab
                   stays where it is rather than being torn down for the ERP. */
                onClick={() =>
                  navigateToVueRoute({ vuePath: crumb.path, openInNewTab: true })
                }
              >
                {crumb.label}
              </button>
            );
          }

          return (
            <li
              key={`${crumb.path}-${idx}`}
              className={BREADCRUMB_CRUMB_BASE + BREADCRUMB_CRUMB_NOT_LAST}
            >
              {inner}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
